import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextCaption, TextTitle } from '@/design-system/components/typography';
import { SafeAreaView, ScrollView, SxProp, View, useDripsyTheme } from 'dripsy';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { WithdrawStackParamList, WithdrawStackRoutes } from './type';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { Xmetadata } from '@utx/utx-access-client';

export interface WithdrawProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const WithdrawNativeMainScreen: React.FC<WithdrawProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const { container, body, textMb, btnView, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<WithdrawStackParamList>>();

  const [xmetadata, setXmetadata] = useState<Array<{ key: string; value: string }>>([]);
  const [account, setAccount] = useState<any>({
    accountId: undefined,
    currency: record?.currency,
    amount: undefined,
    xmetadata: {},
    wallet: undefined,
    name: record?.name
  });
  const [errorMsg, setErrorMsg] = useState<any>({ amount: undefined, accountId: undefined, xmetadata: undefined });

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };

    if ((!keysLength && !account.accountId) || (keys.includes('accountId') && !data?.accountId)) {
      errorMsgFlag.accountId = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && account.accountId) || (keys.includes('accountId') && data?.accountId)) {
      errorMsgFlag.accountId = undefined;
    }

    if ((!keysLength && !account.amount) || (keys.includes('amount') && !data?.amount)) {
      errorMsgFlag.amount = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && account.amount) || (keys.includes('amount') && data?.amount)) {
      if (
        (!keysLength && account.amount && record.available < account.amount) ||
        (keys.includes('amount') && data?.amount && record.available < data?.amount)
      ) {
        errorMsgFlag.amount = t('card-account-management::glossary.insufficient-balance');
        validateFlag = false;
      } else errorMsgFlag.amount = undefined;
    }

    if ((!keysLength && xmetadata.length) || (keys.includes('xmetadata') && xmetadata.length)) {
      let xmetadataErrorMsg: Array<{ key: string; value: string }> = [];
      xmetadata.forEach((x, i) => {
        const xd = xmetadata.find((xm, xmi) => xm.key == x.key && i != xmi);
        if (!x.key) {
          xmetadataErrorMsg[i] = { ...xmetadataErrorMsg[i], key: t('card-account-management::glossary.field-required') };
          validateFlag = false;
        } else if (xd) {
          xmetadataErrorMsg[i] = { ...xmetadataErrorMsg[i], key: t('card-account-management::glossary.key-already-exists') };
          validateFlag = false;
        }
      });

      errorMsgFlag.xmetadata = xmetadataErrorMsg;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeWithdraw = async () => {
    const validate = await validateForm();
    if (validate) {
      xmetadata.forEach((x) => (account.xmetadata[x.key] = x.value));
      onData({ data: account });
      navigation.navigate(WithdrawStackRoutes.Confirm, { onLayout, data: account, func, onReturnData, refresh });
    }
  };

  const onChange = (value: string) => {
    setAccount({ ...account, accountId: value });
  };

  const walletChange = (value: any) => {
    setAccount({ ...account, wallet: value });
  };
  const { theme } = useDripsyTheme();

  return (
    <SafeAreaView sx={container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={{ height: '100%', backgroundColor: '$white' }}>
          <PageHeaderModalNative>
            <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
              <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
                <UniIconButton
                  icon="arrowLeft"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={onClose}></UniIconButton>
              </View>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('card-account-management::withdraw-form.title--withdraw')}
                </TextTitle>
              </View>
            </View>
          </PageHeaderModalNative>
          <View sx={body}>
            <TextTitle sx={textMb} semiBold>
              {t('card-account-management::withdraw-form.text--fill-out-form-to-withdraw-account')}
            </TextTitle>
            <View sx={{ gap: '$5' }}>
              <View sx={{ gap: '$1' }}>
                <TextCaption semiBold sx={{ mb: '$1' }}>
                  {t('card-account-management::glossary.select-account')}
                </TextCaption>
                <AccountSelector
                  asset={record?.currency}
                  checkBalance={false}
                  onValueChange={(value) => {
                    onChange(value);
                    validateForm({ accountId: value });
                  }}
                  onWalletChange={walletChange}
                  value={account?.accountId}
                  sx={{ mb: 0 }}
                />
                {errorMsg?.accountId && (
                  <TextCaption danger sx={{ mt: '$1' }}>
                    {errorMsg?.accountId}
                  </TextCaption>
                )}
              </View>
              <UniInputAmount
                labelText={t('card-account-management::glossary.amount')}
                balance={record.available}
                assetPrefix={false}
                maxFlag={false}
                sxWrap={{ alignItems: 'center', flexDirection: 'row-reverse' }}
                asset={account.currency}
                errorText={errorMsg.amount}
                value={account.amount}
                decimals={2}
                onValueChange={(value) => {
                  setAccount({
                    ...account,
                    amount: value
                  });
                  validateForm({ amount: value?.toString() });
                }}
              />
              <View sx={{ gap: '$1' }}>
                <View sx={{ gap: 10, flexDirection: 'row' }}>
                  <TextCaption semiBold sx={{ mb: '$1' }}>
                    {t('card-account-management::glossary.xmetadata')}
                  </TextCaption>
                  <UniIconButton
                    icon="add"
                    onPress={() => {
                      setXmetadata([...xmetadata, { key: '', value: '' }]);
                    }}
                  />
                </View>
                {xmetadata.map((x, i) => (
                  <View sx={{ gap: '$3', my: '$1', flexDirection: 'row', alignItems: 'center' }}>
                    <UniInput
                      placeholder={t('card-account-management::glossary.please-input-key')}
                      errorText={errorMsg.xmetadata && errorMsg.xmetadata[i] ? errorMsg.xmetadata[i].key : ''}
                      onValueChange={(value) => {
                        let xmetadataFlag = xmetadata;
                        xmetadataFlag[i].key = value || '';
                        setXmetadata(xmetadataFlag);
                        validateForm({ xmetadata: value });
                      }}
                    />
                    <UniInput
                      placeholder={t('card-account-management::glossary.please-input-value')}
                      onValueChange={(value) => {
                        let xmetadataFlag = xmetadata;
                        xmetadataFlag[i].value = value || '';
                        setXmetadata(xmetadataFlag);
                      }}
                      wrapperSx={{ mb: errorMsg.xmetadata && errorMsg.xmetadata[i] && errorMsg.xmetadata[i].key ? '$5' : undefined }}
                    />
                    <UniIconButton
                      icon="minus"
                      onPress={() => {
                        let xmetadataFlag: Array<{ key: string; value: string }> = [];
                        let xmetadataErrorMsg: Array<{ key: string; value: string }> = errorMsg.xmetadata;
                        xmetadata.forEach((xd, xdi) => {
                          if (xdi != i) xmetadataFlag.push(xd);
                          if (xmetadataErrorMsg && xmetadataErrorMsg[i] && xmetadataErrorMsg[i].key) xmetadataErrorMsg[i].key = '';
                        });
                        setXmetadata(xmetadataFlag);
                        setErrorMsg({ ...errorMsg, xmetadata: xmetadataErrorMsg });
                      }}
                      sx={{ mb: errorMsg.xmetadata && errorMsg.xmetadata[i] && errorMsg.xmetadata[i].key ? '$5' : undefined }}
                    />
                  </View>
                ))}
              </View>
            </View>
            <View sx={btnView}>
              <UniButton
                sx={btn}
                labelSx={{ color: '$balck' }}
                label={t('card-account-management::change-atm-pin.button--update')}
                onPress={changeWithdraw}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
